// src/components/OrgManagementPage.js
//
// Phase 7 — Owner/Admin console for managing companies, sites, and user access.
//
//   - Owner: manage all companies, their sites, and all user memberships
//     (including granting owner / tenant-admin).
//   - Tenant admin: manage sites within their own tenant and assign users to
//     those sites.
//
// This page only edits tenant/site METADATA and user_roles membership docs.
// It never reads or writes any patient/PHI collection. Creating a site just
// writes a metadata doc; the new site starts with no patient data.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  Plus,
  Trash2,
  ShieldCheck,
  AlertCircle,
  Mail,
} from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { inviteStaffUser } from "../services/authProvisioning";
import CalendarIntegrationPanel from "./CalendarIntegrationPanel";
import {
  SITE_ROLES,
  listTenants,
  upsertTenant,
  listSites,
  upsertSite,
  listUsers,
  assignUserToSite,
  removeUserFromSite,
  setTenantAdmin,
  setGlobalRole,
} from "../services/membershipService";

const EASE = [0.22, 1, 0.36, 1];

const card =
  "rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-sm p-5 shadow-xl shadow-black/20";
const input =
  "rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none transition-colors duration-200 hover:border-white/20 focus:border-purple-400/50 focus:ring-1 focus:ring-purple-400/30";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418] via-[#0c0726] to-[#04030f]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.18),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.16),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/20 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
  </div>
);

// Small motion-wrapped primary button.
const Btn = ({ children, className = "", ...props }) => (
  <motion.button
    whileHover={{ y: -1 }}
    whileTap={{ scale: 0.97 }}
    transition={{ type: "spring", stiffness: 400, damping: 26 }}
    className={`inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-3.5 py-2 text-sm font-medium text-white shadow-lg shadow-purple-900/30 hover:brightness-110 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {children}
  </motion.button>
);

const section = (i) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: EASE, delay: 0.05 + i * 0.07 },
});

export default function OrgManagementPage() {
  const navigate = useNavigate();
  const {
    tenantId: activeTenantId,
    isOwner,
    isTenantAdmin,
    refreshMembership,
  } = useTenant();

  const [managedTenant, setManagedTenant] = useState(activeTenantId);
  const authorized = isOwner || isTenantAdmin(managedTenant);

  const [tenants, setTenants] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState(""); // success/info (e.g. invite sent)

  // Forms
  const [newTenant, setNewTenant] = useState({ id: "", displayName: "" });
  const [newSite, setNewSite] = useState({ id: "", name: "" });
  const [assign, setAssign] = useState({ email: "", siteId: "", role: "coordinator" });

  const reloadTenants = useCallback(async () => {
    try {
      setTenants(await listTenants());
    } catch (e) {
      setErr(`Could not load companies: ${e.message}`);
    }
  }, []);

  const reloadSitesAndUsers = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([listSites(managedTenant), listUsers()]);
      setSites(s);
      setUsers(u);
    } catch (e) {
      setErr(`Could not load sites/users: ${e.message}`);
    }
  }, [managedTenant]);

  useEffect(() => {
    if (isOwner) reloadTenants();
  }, [isOwner, reloadTenants]);

  useEffect(() => {
    if (authorized) reloadSitesAndUsers();
  }, [authorized, reloadSitesAndUsers]);

  const run = async (fn) => {
    setBusy(true);
    setErr("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleAddTenant = () =>
    run(async () => {
      const id = newTenant.id.trim();
      if (!id) throw new Error("Company id is required.");
      await upsertTenant(id, { displayName: newTenant.displayName.trim() || id });
      setNewTenant({ id: "", displayName: "" });
      await reloadTenants();
      // Select the company we just created so any site added next nests under it
      // (tenants/{id}/sites/...), instead of under the previously-selected one.
      setManagedTenant(id);
    });

  const handleAddSite = () =>
    run(async () => {
      const id = newSite.id.trim();
      if (!id) throw new Error("Site id is required.");
      if (!managedTenant) throw new Error("Select a company first — a site must belong to a company.");
      await upsertSite(managedTenant, id, { name: newSite.name.trim() || id });
      setNewSite({ id: "", name: "" });
      await reloadSitesAndUsers();
    });

  const handleAssign = () =>
    run(async () => {
      const email = assign.email.trim().toLowerCase();
      if (!email || !assign.siteId) throw new Error("Email and site are required.");
      // A brand-new staff member (no user_roles doc yet) needs a login. Assign
      // the role, then provision the Auth account + email a set-password link.
      const isNewUser = !users.some((u) => u.email === email);
      await assignUserToSite(email, managedTenant, assign.siteId, assign.role);
      if (isNewUser) {
        try {
          await inviteStaffUser(email);
          setNotice(`Assigned. A set-password link was emailed to ${email}.`);
        } catch (e) {
          setNotice(
            `Assigned, but the invite email failed (${e.message}). Use "Send login link" on their row to retry.`
          );
        }
      }
      setAssign({ email: "", siteId: "", role: "coordinator" });
      await reloadSitesAndUsers();
      await refreshMembership();
    });

  // Provision/re-send a user's login (set-password) link.
  const handleInvite = (email) =>
    run(async () => {
      const res = await inviteStaffUser(email);
      setNotice(
        res?.created
          ? `Login created — a set-password link was emailed to ${email}.`
          : `A set-password link was re-sent to ${email}.`
      );
    });

  const handleRemove = (email, siteId) =>
    run(async () => {
      await removeUserFromSite(email, managedTenant, siteId);
      await reloadSitesAndUsers();
      await refreshMembership();
    });

  const handleToggleAdmin = (email, makeAdmin) =>
    run(async () => {
      await setTenantAdmin(email, managedTenant, makeAdmin);
      await reloadSitesAndUsers();
      await refreshMembership();
    });

  const handleToggleOwner = (email, makeOwner) =>
    run(async () => {
      await setGlobalRole(email, makeOwner);
      await reloadSitesAndUsers();
      await refreshMembership();
    });

  const tenantOptions = useMemo(
    () => (isOwner ? tenants : [{ id: activeTenantId, displayName: activeTenantId }]),
    [isOwner, tenants, activeTenantId]
  );

  if (!authorized) {
    return (
      <div className="min-h-screen text-slate-200 flex items-center justify-center">
        <PageBackground />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="text-center"
        >
          <ShieldCheck className="w-8 h-8 mx-auto mb-3 text-slate-500" />
          <p>You don't have access to organization management.</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-purple-300 underline hover:text-purple-200">
            Go back
          </button>
        </motion.div>
      </div>
    );
  }

  let s = -1; // section index for stagger

  return (
    <div className="min-h-screen text-slate-100 px-4 py-20">
      <PageBackground />
      <div className="max-w-4xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </motion.button>

        <motion.div {...section(++s)}>
          <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
            {isOwner ? (
              <ShieldCheck className="w-6 h-6 text-purple-300" />
            ) : (
              <Building2 className="w-6 h-6 text-purple-300" />
            )}
            {isOwner ? "Owner Console" : "Site & User Management"}
          </h1>
          <p className="text-sm text-slate-400 mb-6">
            Manage {isOwner ? "companies, " : ""}sites, and user access. Patient
            data is never affected by changes here.
          </p>
        </motion.div>

        <AnimatePresence>
          {err && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: "auto", marginBottom: 16 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tenant selector */}
        <motion.div {...section(++s)} className="mb-6 flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-400">Company</span>
          <div className="relative">
            <select
              value={managedTenant}
              onChange={(e) => setManagedTenant(e.target.value)}
              className={`${input} appearance-none pr-8`}
              disabled={!isOwner}
            >
              {tenantOptions.map((t) => (
                <option key={t.id} value={t.id} className="bg-[#0b0820]">
                  {t.displayName || t.name || t.id}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Companies (owner only) */}
        {isOwner && (
          <motion.section {...section(++s)} className={`${card} mb-6`}>
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-purple-300" /> Companies
            </h2>
            <ul className="mb-4 space-y-1 text-sm">
              {tenants.map((t) => (
                <li
                  key={t.id}
                  className="flex justify-between rounded-md px-2 py-1.5 hover:bg-white/[0.03] transition-colors"
                >
                  <span>{t.displayName || t.name || t.id}</span>
                  <span className="text-slate-500 text-xs font-mono">{t.id}</span>
                </li>
              ))}
              {tenants.length === 0 && <li className="text-slate-500 px-2">No companies yet.</li>}
            </ul>
            <div className="flex flex-wrap gap-2">
              <input
                className={input}
                placeholder="company-id (e.g. lung-innovations)"
                value={newTenant.id}
                onChange={(e) => setNewTenant({ ...newTenant, id: e.target.value })}
              />
              <input
                className={input}
                placeholder="Display name"
                value={newTenant.displayName}
                onChange={(e) => setNewTenant({ ...newTenant, displayName: e.target.value })}
              />
              <Btn onClick={handleAddTenant} disabled={busy}>
                <Plus className="w-4 h-4" /> Add company
              </Btn>
            </div>
          </motion.section>
        )}

        {/* Sites */}
        <motion.section {...section(++s)} className={`${card} mb-6`}>
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-300" /> Sites
            <span className="text-xs font-normal text-slate-400">
              in <span className="font-mono text-slate-300">{managedTenant || "—"}</span>
            </span>
          </h2>
          <ul className="mb-4 space-y-1 text-sm">
            {sites.map((site) => (
              <li
                key={site.id}
                className="flex justify-between rounded-md px-2 py-1.5 hover:bg-white/[0.03] transition-colors"
              >
                <span>{site.name || site.id}</span>
                <span className="text-slate-500 text-xs font-mono">{site.id}</span>
              </li>
            ))}
            {sites.length === 0 && <li className="text-slate-500 px-2">No sites yet.</li>}
          </ul>
          <div className="flex flex-wrap gap-2">
            <input
              className={input}
              placeholder="site-id (e.g. lin-dubois1)"
              value={newSite.id}
              onChange={(e) => setNewSite({ ...newSite, id: e.target.value })}
            />
            <input
              className={input}
              placeholder="Site name"
              value={newSite.name}
              onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
            />
            <Btn onClick={handleAddSite} disabled={busy}>
              <Plus className="w-4 h-4" /> Add site
            </Btn>
          </div>
        </motion.section>

        {/* Per-site integrations. Sits below Sites because it configures a site
            that already exists, and above user access because onboarding a new
            site means: create it → point it at a calendar → add its people. */}
        <motion.section {...section(++s)} className={`${card} mb-6`}>
          <CalendarIntegrationPanel
            tenantId={managedTenant}
            sites={sites}
            busy={busy}
          />
        </motion.section>

        {/* Add a user — tenant admins (non-owners). Deliberately scoped to
            granting site access only; existing users are never modified here.
            Owners get the fuller management panel below instead. */}
        {!isOwner && isTenantAdmin(managedTenant) && (
          <motion.section {...section(++s)} className={card}>
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-300" /> User access
            </h2>
            <p className="text-xs text-slate-400 mb-3">
              Add a person to one of your sites, or adjust an existing user's
              access below. Re-adding an email with a different role updates that
              site's role. Admins and owners can't be changed here.
            </p>

            {notice && (
              <div className="mb-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                {notice}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <input
                className={input}
                placeholder="user@email.com"
                value={assign.email}
                onChange={(e) => setAssign({ ...assign, email: e.target.value })}
              />
              <select
                className={`${input} appearance-none pr-8`}
                value={assign.siteId}
                onChange={(e) => setAssign({ ...assign, siteId: e.target.value })}
              >
                <option value="" className="bg-[#0b0820]">Select site…</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id} className="bg-[#0b0820]">
                    {site.name || site.id}
                  </option>
                ))}
              </select>
              <select
                className={`${input} appearance-none pr-8`}
                value={assign.role}
                onChange={(e) => setAssign({ ...assign, role: e.target.value })}
              >
                {/* Admins can't hand out the "admin" role — that would grant
                    full admin access. Owners still can, in their panel below. */}
                {SITE_ROLES.filter((r) => r !== "admin").map((r) => (
                  <option key={r} value={r} className="bg-[#0b0820]">{r}</option>
                ))}
              </select>
              <Btn onClick={handleAssign} disabled={busy}>
                <Plus className="w-4 h-4" /> Add user
              </Btn>
            </div>

            {/* Existing users in this tenant. An admin can manage regular
                users' site access, but admins/owners are shown read-only —
                an admin can never change another admin (also enforced in
                Firestore rules). */}
            <div className="mt-5 space-y-2 text-sm">
              <AnimatePresence initial={false}>
                {users.map((u) => {
                  const membership = u.memberships?.[managedTenant];
                  const siteEntries = Object.entries(membership?.sites || {});
                  // Only users with access in THIS company (owners always shown).
                  if (!membership && u.globalRole !== "owner") return null;
                  const isOwnerUser = u.globalRole === "owner";
                  const isAdminUser = membership?.tenantRole === "admin";
                  const locked = isOwnerUser || isAdminUser; // can't be changed by an admin
                  return (
                    <motion.div
                      key={u.email}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25, ease: EASE }}
                      className="rounded-xl border border-white/5 bg-white/[0.02] p-3 hover:border-white/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{u.email}</span>
                        <div className="flex items-center gap-2">
                          {isOwnerUser && (
                            <span className="rounded-full bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 text-[10px] font-medium text-purple-200">
                              OWNER
                            </span>
                          )}
                          {isAdminUser && (
                            <span className="rounded-full bg-blue-500/20 border border-blue-400/30 px-2 py-0.5 text-[10px] font-medium text-blue-200">
                              ADMIN
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {siteEntries.map(([sid, role]) => (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] border border-white/10 px-2 py-0.5 text-xs"
                          >
                            {(sites.find((x) => x.id === sid)?.name) || sid}: {role}
                            {!locked && (
                              <button
                                onClick={() => handleRemove(u.email, sid)}
                                disabled={busy}
                                className="text-red-300/80 hover:text-red-200 transition-colors"
                                title="Remove access"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        {siteEntries.length === 0 && (
                          <span className="text-slate-500 text-xs">No site access in this company.</span>
                        )}
                      </div>
                      {locked ? (
                        <div className="mt-2.5 text-xs text-slate-500">
                          You can't change {isOwnerUser ? "an owner" : "another admin"}.
                        </div>
                      ) : (
                        <div className="mt-2.5 flex gap-4 text-xs">
                          <button
                            onClick={() => handleInvite(u.email)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 hover:underline transition-colors"
                            title="Create the login if needed and email a set-password link"
                          >
                            <Mail className="w-3 h-3" /> Send login link
                          </button>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.section>
        )}

        {/* Users — owner-only (full membership management panel) */}
        {isOwner && (
          <motion.section {...section(++s)} className={card}>
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-300" /> User access
            </h2>

            {notice && (
              <div className="mb-3 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                {notice}
              </div>
            )}

            {/* Assign */}
            <div className="flex flex-wrap gap-2 mb-4">
              <input
                className={input}
                placeholder="user@email.com"
                value={assign.email}
                onChange={(e) => setAssign({ ...assign, email: e.target.value })}
              />
              <select
                className={`${input} appearance-none pr-8`}
                value={assign.siteId}
                onChange={(e) => setAssign({ ...assign, siteId: e.target.value })}
              >
                <option value="" className="bg-[#0b0820]">Select site…</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id} className="bg-[#0b0820]">
                    {site.name || site.id}
                  </option>
                ))}
              </select>
              <select
                className={`${input} appearance-none pr-8`}
                value={assign.role}
                onChange={(e) => setAssign({ ...assign, role: e.target.value })}
              >
                {SITE_ROLES.map((r) => (
                  <option key={r} value={r} className="bg-[#0b0820]">{r}</option>
                ))}
              </select>
              <Btn onClick={handleAssign} disabled={busy}>
                <Plus className="w-4 h-4" /> Assign
              </Btn>
            </div>

            {/* Existing users for this tenant */}
            <div className="space-y-2 text-sm">
              <AnimatePresence initial={false}>
                {users.map((u) => {
                  const membership = u.memberships?.[managedTenant];
                  const siteEntries = Object.entries(membership?.sites || {});
                  const tenantAdmin = membership?.tenantRole === "admin";
                  if (!membership && u.globalRole !== "owner") return null;
                  return (
                    <motion.div
                      key={u.email}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25, ease: EASE }}
                      className="rounded-xl border border-white/5 bg-white/[0.02] p-3 hover:border-white/10 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{u.email}</span>
                        <div className="flex items-center gap-2">
                          {u.globalRole === "owner" && (
                            <span className="rounded-full bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 text-[10px] font-medium text-purple-200">
                              OWNER
                            </span>
                          )}
                          {tenantAdmin && (
                            <span className="rounded-full bg-blue-500/20 border border-blue-400/30 px-2 py-0.5 text-[10px] font-medium text-blue-200">
                              ADMIN
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {siteEntries.map(([sid, role]) => (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] border border-white/10 px-2 py-0.5 text-xs"
                          >
                            {(sites.find((x) => x.id === sid)?.name) || sid}: {role}
                            <button
                              onClick={() => handleRemove(u.email, sid)}
                              disabled={busy}
                              className="text-red-300/80 hover:text-red-200 transition-colors"
                              title="Remove access"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        {siteEntries.length === 0 && (
                          <span className="text-slate-500 text-xs">No site access in this company.</span>
                        )}
                      </div>
                      <div className="mt-2.5 flex gap-4 text-xs">
                        <button
                          onClick={() => handleInvite(u.email)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 hover:underline transition-colors"
                          title="Create the login if needed and email a set-password link"
                        >
                          <Mail className="w-3 h-3" /> Send login link
                        </button>
                        <button
                          onClick={() => handleToggleAdmin(u.email, !tenantAdmin)}
                          disabled={busy}
                          className="text-blue-300 hover:text-blue-200 hover:underline transition-colors"
                        >
                          {tenantAdmin ? "Revoke admin" : "Make admin"}
                        </button>
                        {isOwner && (
                          <button
                            onClick={() => handleToggleOwner(u.email, u.globalRole !== "owner")}
                            disabled={busy}
                            className="text-purple-300 hover:text-purple-200 hover:underline transition-colors"
                          >
                            {u.globalRole === "owner" ? "Revoke owner" : "Make owner"}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}
